/**
 * Server-Sent Events (SSE) service for real-time updates
 */

import { useAuthStore } from '../store/authStore';
import { LoggerService } from './LoggerService';

export interface SSEEvent {
  type: string;
  data: any;
}

class SSEService {
  private eventSource: EventSource | null = null;
  private listeners: Map<string, Set<(data: any) => void>> = new Map();
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 3000;

  connect(userId: string): void {
    if (this.eventSource) {
      this.disconnect();
    }

    // Get token from auth store
    const authStore = useAuthStore.getState();
    const token = authStore.accessToken;

    // Note: EventSource doesn't support custom headers, so authentication
    // must be handled via query parameter
    const url = token 
      ? `/api/v1/dashboard/events?token=${encodeURIComponent(token)}`
      : `/api/v1/dashboard/events`;
    
    LoggerService.debug('[SSE] Connecting to dashboard events', { hasToken: !!token });
    
    this.eventSource = new EventSource(url, {
      withCredentials: true,
    });

    this.eventSource.onopen = () => {
      LoggerService.debug('[SSE] Connected to dashboard events');
      this.reconnectAttempts = 0;
    };

    this.eventSource.onmessage = (event) => {
      try {
        const sseEvent: SSEEvent = JSON.parse(event.data);
        this.handleEvent(sseEvent);
      } catch (error) {
        LoggerService.error('[SSE] Error parsing event:', error);
      }
    };

    this.eventSource.onerror = (error) => {
      LoggerService.error('[SSE] Connection error:', error);
      this.handleReconnect(userId);
    };
  }

  private handleReconnect(userId: string): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      LoggerService.error('[SSE] Max reconnect attempts reached');
      this.disconnect();
      return;
    }

    this.reconnectAttempts++;
    setTimeout(() => {
      LoggerService.debug(`[SSE] Reconnecting (attempt ${this.reconnectAttempts})...`);
      this.connect(userId);
    }, this.reconnectDelay);
  }

  private handleEvent(event: SSEEvent): void {
    const listeners = this.listeners.get(event.type);
    if (listeners) {
      listeners.forEach((listener) => {
        try {
          listener(event.data);
        } catch (error) {
          LoggerService.error(`[SSE] Error in listener for ${event.type}:`, error);
        }
      });
    }

    // Also notify 'all' listeners
    const allListeners = this.listeners.get('all');
    if (allListeners) {
      allListeners.forEach((listener) => {
        try {
          listener(event);
        } catch (error) {
          LoggerService.error('[SSE] Error in "all" listener:', error);
        }
      });
    }
  }

  on(eventType: string, callback: (data: any) => void): void {
    if (!this.listeners.has(eventType)) {
      this.listeners.set(eventType, new Set());
    }
    this.listeners.get(eventType)!.add(callback);
  }

  off(eventType: string, callback: (data: any) => void): void {
    const listeners = this.listeners.get(eventType);
    if (listeners) {
      listeners.delete(callback);
      if (listeners.size === 0) {
        this.listeners.delete(eventType);
      }
    }
  }

  disconnect(): void {
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }
    this.listeners.clear();
    LoggerService.debug('[SSE] Disconnected');
  }

  isConnected(): boolean {
    return this.eventSource !== null && this.eventSource.readyState === EventSource.OPEN;
  }
}

export const sseService = new SSEService();

